<template>
  <div class="space-y-5">
    <!-- The record we're matching. -->
    <div>
      <div class="flex items-baseline gap-2 flex-wrap">
        <h2 class="text-lg font-semibold text-gray-800">{{ company.company_name }}</h2>
        <span class="text-xs text-gray-400">id {{ company.id }}</span>
      </div>
      <div v-if="company.doing_business_as" class="text-sm text-gray-600">dba {{ company.doing_business_as }}</div>
      <div v-if="company.other_names.length" class="text-xs text-gray-500">also {{ company.other_names.join(' · ') }}</div>
      <div class="mt-1 text-sm text-gray-700">{{ company.full_address || 'No address on record' }}</div>
      <div class="text-xs text-gray-500 mt-0.5 space-x-2">
        <span v-if="company.phone">{{ company.phone }}</span>
        <button
          v-if="company.cage_code"
          type="button"
          class="hover:text-indigo-700 hover:underline"
          title="Copy the CAGE code"
          @click="copyCage(company.cage_code)"
        >CAGE {{ company.cage_code }} ⧉</button>
        <span>Run {{ review.run_id }} · {{ review.kind === 'review' ? 'needs review' : 'no match found' }}</span>
      </div>
      <div v-if="company.industries" class="text-xs text-gray-500 mt-1">{{ company.industries }}</div>
      <div class="flex flex-wrap gap-2 mt-2 text-xs">
        <a
          :href="googleSearchUrl(company)"
          target="_blank"
          rel="noopener noreferrer"
          class="px-2 py-0.5 rounded-md border border-sky-200 bg-sky-100 text-sky-800 hover:bg-sky-200"
          title="Search Google for the company's name and address"
        >Google ↗</a>
        <a
          v-for="link in cageLinks(company.cage_code)"
          :key="link.label"
          :href="link.url"
          target="_blank"
          rel="noopener noreferrer"
          class="px-2 py-0.5 rounded-md border border-sky-200 bg-sky-100 text-sky-800 hover:bg-sky-200"
          :title="`CAGE ${company.cage_code} on ${link.label}`"
        >{{ link.label }} ↗</a>
      </div>
    </div>

    <!-- What the run would have picked, and why. -->
    <div v-if="review.proposed_url || review.proposed_reason" class="rounded-md border border-gray-200 bg-white shadow-sm px-3 py-2">
      <div class="text-xs uppercase tracking-widest text-gray-500 mb-1">The run's proposal</div>
      <div v-if="review.proposed_url" class="flex items-center gap-2 text-sm">
        <a :href="review.proposed_url" target="_blank" rel="noopener noreferrer" class="text-indigo-700 hover:underline break-all">{{ review.proposed_url }}</a>
        <ConfidencePill :value="review.proposed_confidence" />
      </div>
      <div v-else class="text-sm text-gray-500">Nothing proposed.</div>
      <div v-if="review.proposed_reason" class="text-sm text-gray-700 mt-1">{{ review.proposed_reason }}</div>
    </div>

    <div v-if="review.injection_flags.length" class="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
      A candidate page contains text aimed at AI agents ({{ review.injection_flags.join(', ') }}).
      Check it by hand rather than trusting the run's reasoning.
    </div>

    <!-- What the CAGE registry lists for this company. -->
    <div v-if="cage" class="rounded-md border border-gray-200 bg-white shadow-sm px-3 py-2 text-sm">
      <div class="text-xs uppercase tracking-widest text-gray-500 mb-1">
        CAGE record
        <button
          type="button"
          class="tracking-widest hover:text-indigo-700 hover:underline"
          title="Copy the CAGE code"
          @click="copyCage(cage.cage_code)"
        >{{ cage.cage_code }} ⧉</button>
      </div>
      <div v-if="cage.error" class="text-gray-500">The lookup failed ({{ cage.error }}); not evidence either way.</div>
      <div v-else-if="!cage.found" class="text-gray-500">No page for this code; not evidence either way.</div>
      <template v-else>
        <div class="text-gray-800">{{ cage.page_name }}<span v-if="cage.location" class="text-gray-500"> · {{ cage.location }}</span></div>
        <div v-for="street in cage.streets" :key="street" class="text-xs text-gray-600">{{ street }}</div>
        <div class="text-xs mt-1">
          <span class="text-gray-500">Registered website: </span>
          <template v-if="cage.sites.length">
            <a
              v-for="site in cage.sites"
              :key="site"
              :href="withScheme(site)"
              target="_blank"
              rel="noopener noreferrer"
              class="text-indigo-700 hover:underline mr-2"
            >{{ site }}</a>
          </template>
          <span v-else class="text-gray-500">none</span>
        </div>
        <div v-if="cage.emails.length" class="text-xs">
          <span class="text-gray-500">Emails: </span>{{ cage.emails.join(', ') }}
        </div>
      </template>
    </div>

    <!-- Every site the run looked at. Click one to choose it; 1-9 on the keyboard. -->
    <div>
      <div class="text-xs uppercase tracking-widest text-gray-500 mb-2">
        Candidates <span class="normal-case tracking-normal text-gray-400">· click one to choose it</span>
      </div>
      <div v-if="!reachable.length" class="text-sm text-gray-500 italic">
        {{ zeroScored ? 'The model scored every site it found 0.' : 'The run found no sites to check.' }}
      </div>
      <div
        v-for="(c, position) in reachable"
        :key="c.index"
        class="border rounded-md p-3 mb-2 cursor-pointer transition-colors"
        :class="pickedIndex === c.index ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-400 shadow-sm' : 'border-gray-200 bg-white shadow-sm hover:border-indigo-300'"
        @click="decidable && $emit('pick', c.index)"
      >
        <div class="flex items-start justify-between gap-3">
          <div class="min-w-0">
            <div class="flex items-center gap-2 flex-wrap">
              <span class="text-xs text-gray-400 w-4">{{ position < 9 ? position + 1 : '' }}</span>
              <span class="font-medium text-gray-800">{{ c.domain }}</span>
              <a
                :href="c.url"
                target="_blank"
                rel="noopener noreferrer"
                class="text-xs text-indigo-600 hover:underline"
                @click.stop
              >open ↗</a>
              <span v-if="c.model_chosen" class="px-1.5 py-0.5 rounded-full text-xs bg-indigo-100 text-indigo-700">run's pick</span>
              <span v-if="c.tier === 'cage_site_field'" class="px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">CAGE-registered</span>
              <span v-if="c.fetch_status && c.fetch_status !== 'ok'" class="px-1.5 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">{{ c.fetch_status }}</span>
              <span v-if="c.injection_flags.length" class="px-1.5 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">text aimed at AI</span>
            </div>
            <div v-if="c.page_name || c.page_title" class="text-sm text-gray-600 truncate pl-6">
              {{ c.page_name }}<span v-if="c.page_name && c.page_title && c.page_title !== c.page_name" class="text-gray-400"> — </span>{{ c.page_title !== c.page_name ? c.page_title : '' }}
            </div>
            <div v-if="signals(c).length" class="flex flex-wrap gap-1 mt-1 pl-6">
              <span
                v-for="s in signals(c)"
                :key="s.key"
                class="px-1.5 py-0.5 rounded text-xs"
                :class="s.good ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                :title="s.evidence"
              >{{ s.label }}</span>
            </div>
            <div v-if="c.model_disqualifiers.length" class="text-xs text-red-700 mt-1 pl-6">{{ c.model_disqualifiers.join('; ') }}</div>
          </div>
          <ConfidencePill :value="c.model_confidence" class="shrink-0" />
        </div>
      </div>
    </div>

    <!-- Sites that wouldn't load but have pages in the search index: leads to
         check by hand, not evidence of a match. -->
    <div v-if="review.unfetchable_but_indexed.length" class="text-sm">
      <div class="text-xs uppercase tracking-widest text-gray-500 mb-1">Wouldn't load, but are indexed</div>
      <div v-for="u in review.unfetchable_but_indexed" :key="u.domain" class="text-xs text-gray-700">
        <a :href="withScheme(u.domain)" target="_blank" rel="noopener noreferrer" class="text-indigo-700 hover:underline">{{ u.domain }}</a>
        <span class="text-gray-500"> · {{ u.fetch_status }} · {{ u.indexed_pages }} indexed page{{ u.indexed_pages === '1' ? '' : 's' }}</span>
        <span v-if="u.index_title" class="text-gray-500"> · {{ u.index_title }}</span>
      </div>
    </div>

    <!-- What has happened to this row. -->
    <div v-if="review.history.length" class="text-xs text-gray-500">
      <div class="uppercase tracking-widest mb-1">History</div>
      <div v-for="(h, i) in review.history" :key="i">
        {{ formatWhen(h.at) }} · {{ h.action }}<template v-if="h.decision"> {{ h.decision === 'url' ? h.final_url : 'no website' }}</template>
        · {{ h.source }}<template v-if="h.reasoning"> · {{ h.reasoning }}</template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps, defineEmits } from 'vue'
import ConfidencePill from './ConfidencePill.vue'
import { googleSearchUrl, cageLinks } from './links'
import { visibleCandidates, zeroScoredCount } from './candidates'
import { useToast } from 'vue-toast-notification'

const $toast = useToast()

async function copyCage(code) {
  try {
    await navigator.clipboard.writeText(code)
    $toast.open({ message: `CAGE ${code} copied`, type: 'success', duration: 1500 })
  } catch (e) {
    $toast.open({ message: 'Copy failed', type: 'error', duration: 2000 })
  }
}

const props = defineProps({
  review: { type: Object, required: true },
  pickedIndex: { type: Number, default: null },
  decidable: { type: Boolean, default: false },
})
defineEmits(['pick'])

const company = computed(() => props.review.company || {})
const cage = computed(() => props.review.cage_record)

const reachable = computed(() => visibleCandidates(props.review))
const zeroScored = computed(() => zeroScoredCount(props.review))

const CRITERIA_LABELS = {
  name: 'name', location: 'location', address: 'address', phone: 'phone', industry: 'industry',
  logo: 'logo', plausibility: 'plausible', cage: 'CAGE', uei: 'UEI', domain: 'domain',
  email_domain: 'email domain',
}

// The model's and the matcher's evaluated criteria, as good/bad chips. Ones
// neither could evaluate (or rated neutral) are left out.
function signals(c) {
  const out = []
  const seen = new Set()
  const add = (source, ratings, evidence = {}) => {
    for (const [key, rating] of Object.entries(ratings || {})) {
      const good = rating === 'positive' || rating === 'strong-positive'
      const bad = rating === 'negative' || rating === 'strong-negative'
      if ((!good && !bad) || seen.has(key)) continue
      seen.add(key)
      out.push({
        key: `${source}-${key}`, good,
        label: `${good ? '✓' : '✗'} ${CRITERIA_LABELS[key] || key}`,
        evidence: evidence[key] || rating,
      })
    }
  }
  add('model', c.model_criteria)
  add('matcher', c.matcher_ratings, c.matcher_evidence)
  return out
}

function withScheme(host) {
  return /^https?:\/\//i.test(host) ? host : `https://${host}`
}

function formatWhen(iso) {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch (e) { return iso }
}
</script>
